import { useEffect, useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  FileText,
  AlertTriangle,
  Settings,
  Server,
  LogOut,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Shield,
  Users,
  UserPlus,
  X,
  LucideIcon,
  FolderKanban,
  List,
  GitBranch,
  Calendar,
  Share2,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { authStore } from '@/lib/authStore';
import { QUERY_KEYS } from '@/lib/queryKeys';
import { clearUserThemeStorage } from '@/hooks/useUserTheme';
import { useBrand } from '@/hooks/useBrand';
import { useMyPermissions, resolveMenuPath } from '@/hooks/usePermissions';
import { useMyMenu } from '@/hooks/useMyMenu';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { emitLogout } from '@/lib/auth/sessionCoordinator';

const iconMap: Record<string, LucideIcon> = {
  LayoutDashboard,
  FileText,
  AlertTriangle,
  Settings,
  Server,
  Shield,
  Users,
  UserPlus,
  FolderKanban,
  List,
  GitBranch,
  Calendar,
  Share2,
};

interface AdminSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
}

const navItemBase =
  'relative flex items-center gap-2 rounded-md px-2.5 py-2 text-sm transition-colors duration-150';
const navItemActive = 'bg-primary/10 text-primary font-medium border-l-2 border-primary';
const navItemInactive = 'text-muted-foreground hover:bg-muted hover:text-foreground border-l-2 border-transparent';

export const AdminSidebar = ({ isOpen, onClose, collapsed, setCollapsed }: AdminSidebarProps) => {
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.matchMedia('(min-width: 1024px)').matches;
  });
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { name: siteTitle, logoSrc } = useBrand();
  const { canView, isAdmin } = useMyPermissions();
  const { data: menuItems = [] } = useMyMenu();

  useEffect(() => {
    const activeParentIds: string[] = [];

    menuItems.forEach((item) => {
      if (item.children) {
        const isChildActive = item.children.some((child) => isPathActive(location.pathname, child.path));
        const isUnderParent = isPathActive(location.pathname, item.path);
        if (isChildActive || isUnderParent) {
          activeParentIds.push(item.id);
        }
      }
    });

    setExpandedMenus((prev) => {
      const same =
        prev.length === activeParentIds.length && prev.every((id, idx) => id === activeParentIds[idx]);
      return same ? prev : activeParentIds;
    });
  }, [location.pathname, menuItems]);

  const toggleSubmenu = (itemId: string) => {
    setExpandedMenus((prev) =>
      prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId],
    );
  };

  useEffect(() => {
    const mql = window.matchMedia('(min-width: 1024px)');
    const onChange = () => setIsDesktop(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  const handleLogout = () => {
    const userId = authStore.getState().user?.id;
    if (userId) clearUserThemeStorage(userId);
    queryClient.removeQueries({ queryKey: QUERY_KEYS.menu.myMenu });
    queryClient.removeQueries({ queryKey: ['my-permissions'], exact: false });
    queryClient.removeQueries({ queryKey: QUERY_KEYS.settings.userTheme(userId ?? ''), exact: false });
    emitLogout('AdminSidebar:handleLogout');
  };

  const handleNavClick = () => {
    if (!isDesktop) {
      onClose();
    }
  };

  const getIcon = (iconName: string): LucideIcon => iconMap[iconName] || LayoutDashboard;

  const normalizeNavPath = (path: string): string => {
    if (path.startsWith('/projects/duty-schedules')) {
      return path.split('?')[0];
    }
    return path;
  };

  const isPathActive = (pathname: string, targetPath: string): boolean => {
    const normalizedTargetPath = normalizeNavPath(targetPath);
    if (normalizedTargetPath === '/') return pathname === '/';
    return pathname === normalizedTargetPath || pathname.startsWith(`${normalizedTargetPath}/`);
  };

  const renderSubmenuItems = (visibleChildren: typeof menuItems[0]['children'], isDropdown = false) => {
    if (!visibleChildren) return null;
    return visibleChildren.map((child) => {
      const normalizedChildPath = normalizeNavPath(child.path);
      const isChildActive = isPathActive(location.pathname, normalizedChildPath);
      const ChildIcon = getIcon(child.icon);

      if (isDropdown) {
        return (
          <DropdownMenuItem
            key={child.id}
            className={cn(
              'cursor-pointer text-sm font-medium focus:bg-primary/10 focus:text-primary',
              isChildActive ? 'bg-primary/10 text-primary' : 'text-muted-foreground',
            )}
            onClick={() => {
              navigate(normalizedChildPath);
              handleNavClick();
            }}
          >
            <div className="flex items-center gap-2 w-full">
              <ChildIcon className="w-3.5 h-3.5" />
              <span>{child.label}</span>
            </div>
          </DropdownMenuItem>
        );
      }

      return (
        <li key={child.id}>
          <NavLink
            to={normalizedChildPath}
            end={normalizedChildPath === '/permissions'}
            onClick={handleNavClick}
            className={cn(
              'flex items-center gap-2 rounded-md px-2.5 py-2 text-sm transition-colors',
              isChildActive ? navItemActive : navItemInactive,
            )}
          >
            <ChildIcon className="w-4 h-4 shrink-0" />
            <span>{child.label}</span>
          </NavLink>
        </li>
      );
    });
  };

  return (
    <>
      {isOpen && (
        <div
          onClick={onClose}
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          aria-hidden
        />
      )}

      <aside
        className={cn(
          'fixed left-0 top-0 z-50 flex h-screen flex-col border-r border-jira-border bg-jira-surface shadow-none',
          'transition-[width,transform] duration-300 ease-out',
          isDesktop
            ? collapsed
              ? 'w-[88px]'
              : 'w-[280px]'
            : cn('w-[280px]', isOpen ? 'translate-x-0' : '-translate-x-full'),
        )}
      >
        <div className="border-b border-jira-border p-3 lg:p-4">
          <div className="flex items-center justify-between">
            <div className="flex min-w-0 items-center gap-2">
              {logoSrc ? (
                <img
                  src={logoSrc}
                  alt="Logo"
                  className="h-8 w-8 shrink-0 rounded-md object-contain lg:h-9 lg:w-9"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              ) : (
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-jira-border bg-muted/30 lg:h-9 lg:w-9">
                  <span className="text-[10px] font-semibold text-muted-foreground">ISOFH</span>
                </div>
              )}
              {!collapsed && (
                <span className="truncate text-sm font-semibold text-jira-text lg:text-base">{siteTitle}</span>
              )}
            </div>

            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-1.5 hover:bg-muted lg:hidden"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto p-2 lg:p-3">
          <ul className="space-y-0.5">
            {menuItems
              .filter((item) => {
                if (isAdmin) return true;
                const hasChildren = item.children && item.children.length > 0;
                if (hasChildren) {
                  return item.children!.some((child) => canView(resolveMenuPath(child.path)));
                }
                return canView(resolveMenuPath(item.path));
              })
              .map((item) => {
                const hasChildren = item.children && item.children.length > 0;
                const visibleChildren = hasChildren
                  ? item.children!.filter((child) => isAdmin || canView(resolveMenuPath(child.path)))
                  : [];

                if (hasChildren && visibleChildren.length === 0) {
                  return null;
                }

                const isExpanded = expandedMenus.includes(item.id);
                const normalizedItemPath = normalizeNavPath(item.path);
                const isActive = hasChildren
                  ? visibleChildren.some((child) => isPathActive(location.pathname, child.path))
                  : isPathActive(location.pathname, normalizedItemPath);
                const IconComponent = getIcon(item.icon);

                return (
                  <li key={item.id}>
                    {hasChildren ? (
                      <div>
                        {collapsed ? (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                type="button"
                                className={cn(
                                  'flex w-full items-center justify-center rounded-md p-2 transition-colors',
                                  isActive ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-muted',
                                )}
                              >
                                <IconComponent className="h-4 w-4" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              side="right"
                              align="start"
                              sideOffset={12}
                              className="w-56 border-jira-border bg-jira-surface p-1.5 shadow-panel"
                            >
                              <div className="mb-1 border-b border-jira-border/60 px-2 py-1.5">
                                <span className="text-xs font-medium text-jira-textSubtle">{item.label}</span>
                              </div>
                              {renderSubmenuItems(visibleChildren, true)}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        ) : (
                          <button
                            type="button"
                            onClick={() => toggleSubmenu(item.id)}
                            className={cn(
                              navItemBase,
                              'w-full justify-between',
                              isActive ? navItemActive : navItemInactive,
                            )}
                          >
                            <span className="flex items-center gap-2">
                              <IconComponent className="h-4 w-4 shrink-0" />
                              <span className="font-medium">{item.label}</span>
                            </span>
                            <ChevronDown
                              className={cn(
                                'h-4 w-4 shrink-0 transition-transform duration-200',
                                isExpanded && 'rotate-180',
                              )}
                            />
                          </button>
                        )}

                        {isExpanded && !collapsed && (
                          <ul className="ml-3 mt-1 space-y-0.5 border-l border-jira-border pl-2">
                            {renderSubmenuItems(visibleChildren)}
                          </ul>
                        )}
                      </div>
                    ) : (
                      <NavLink
                        to={normalizedItemPath}
                        end={normalizedItemPath === '/'}
                        onClick={handleNavClick}
                        title={collapsed ? item.label : undefined}
                        className={cn(
                          collapsed ? 'flex justify-center rounded-md p-2' : navItemBase,
                          isActive ? navItemActive : navItemInactive,
                        )}
                      >
                        <IconComponent className="h-4 w-4 shrink-0" />
                        {!collapsed && <span className="font-medium">{item.label}</span>}
                      </NavLink>
                    )}
                  </li>
                );
              })
              .filter(Boolean)}
          </ul>
        </nav>

        <div className="border-t border-jira-border p-2 lg:p-3">
          <button
            type="button"
            onClick={handleLogout}
            className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-sm font-medium text-destructive transition-colors hover:bg-destructive/10"
          >
            <div className="rounded-md bg-destructive/10 p-1.5">
              <LogOut className="h-4 w-4" />
            </div>
            {!collapsed && <span>Đăng xuất</span>}
          </button>

          <button
            type="button"
            onClick={() => setCollapsed(!collapsed)}
            className="mt-2 hidden w-full items-center justify-center rounded-md py-1.5 text-muted-foreground transition-colors hover:bg-muted lg:flex"
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </button>
        </div>
      </aside>
    </>
  );
};
